import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface StripeB2BCheckoutFormProps {
  planId: string;
  billingPeriod: 'monthly' | 'annually';
  controllersCount: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const StripeB2BCheckoutForm: React.FC<StripeB2BCheckoutFormProps> = ({
  planId,
  billingPeriod,
  controllersCount,
  onSuccess,
  onCancel
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    company_address: {
      street: '',
      postcode: '',
      city: '',
      country: '',
      state: ''
    },
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    tax_id: ''
  });
  
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [debugMessages, setDebugMessages] = useState<string[]>([]);
  
  // Add debug message
  const addDebugMessage = (message: string) => {
    setDebugMessages(prev => [...prev, message]);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Handle nested company_address fields
    if (name.startsWith('company_address.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        company_address: {
          ...prev.company_address,
          [fieldName]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    // Required fields validation
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
      isValid = false;
      addDebugMessage('Company name is missing');
    }
    
    if (!formData.company_address.street.trim()) {
      newErrors['company_address.street'] = 'Street address is required';
      isValid = false;
      addDebugMessage('Street address is missing');
    }
    
    if (!formData.company_address.postcode.trim()) {
      newErrors['company_address.postcode'] = 'Postcode is required';
      isValid = false;
      addDebugMessage('Postcode is missing');
    }
    
    if (!formData.company_address.city.trim()) {
      newErrors['company_address.city'] = 'City is required';
      isValid = false;
      addDebugMessage('City is missing');
    }
    
    if (!formData.company_address.country.trim()) {
      newErrors['company_address.country'] = 'Country is required';
      isValid = false;
      addDebugMessage('Country is missing');
    }
    
    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
      isValid = false;
      addDebugMessage('Contact name is missing');
    }
    
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Email is required';
      isValid = false;
      addDebugMessage('Email is missing');
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
      isValid = false;
      addDebugMessage('Invalid email format');
    }
    
    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Phone number is required';
      isValid = false;
      addDebugMessage('Phone number is missing');
    }
    
    if (!formData.tax_id.trim()) {
      newErrors.tax_id = 'Tax ID is required';
      isValid = false;
      addDebugMessage('Tax ID is missing');
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleCaptchaChange = (value: string) => {
    // This would be called by the actual captcha component
    setCaptchaVerified(!!value);
    if (value) {
      addDebugMessage('Captcha verified successfully');
    } else {
      addDebugMessage('Captcha verification failed or expired');
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addDebugMessage('Form validation failed');
      return;
    }
    
    if (!captchaVerified) {
      setErrors(prev => ({ ...prev, captcha: 'Please complete the captcha verification' }));
      addDebugMessage('Captcha not verified');
      return;
    }
    
    setIsSubmitting(true);
    addDebugMessage('Submitting checkout form...');
    
    try {
      // Prepare checkout data
      const checkoutData = {
        plan_id: planId,
        billing_period: billingPeriod,
        company_name: formData.company_name,
        company_address: formData.company_address,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        tax_id: formData.tax_id,
        currency: 'EUR',
        controllers_count: controllersCount,
        metadata: {
          source: 'web_checkout'
        }
      };
      
      addDebugMessage('Calling checkout API...');
      
      // Call the checkout API
      const response = await apiService.post('/api/v1/stripe/b2b/checkout', checkoutData) as any;
      
      if (response?.success) {
        addDebugMessage('Checkout successful!');
        
        // Handle successful checkout
        if (onSuccess) {
          onSuccess();
        }
        
        // Navigate to payment page or show success message
        navigate('/payment-success', {
          state: {
            paymentIntentId: response.data.payment_intent_id,
            invoiceId: response.data.invoice_id
          }
        });
      } else {
        setErrors(prev => ({ ...prev, api: response.error_message || 'Checkout failed' }));
        addDebugMessage(`Checkout failed: ${response.error_message}`);
      }
    } catch (error: any) {
      setErrors(prev => ({ ...prev, api: error.message || 'An error occurred during checkout' }));
      addDebugMessage(`Error during checkout: ${error.message}`);
      // Don't reload, just show the error to the user
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Check if form is valid for button enablement
  const isFormValid = () => {
    const hasRequiredFields = (
      formData.company_name &&
      formData.company_address.street &&
      formData.company_address.postcode &&
      formData.company_address.city &&
      formData.company_address.country &&
      formData.contact_name &&
      formData.contact_email &&
      formData.contact_phone &&
      formData.tax_id
    );
    
    return hasRequiredFields && captchaVerified && Object.keys(errors).length === 0;
  };
  
  return (
    <div className="stripe-b2b-checkout-form">
      <h2 className="text-xl font-semibold mb-4">Complete Your Subscription</h2>
      
      {/* Summary Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-2">Subscription Summary</h3>
        <p><strong>Plan:</strong> {planId}</p>
        <p><strong>Billing Period:</strong> {billingPeriod}</p>
        <p><strong>Controllers:</strong> {controllersCount}</p>
      </div>
      
      {/* Debug Messages */}
      {debugMessages.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <h4 className="font-medium text-yellow-800 mb-2">Debug Information:</h4>
          <ul className="text-sm text-yellow-700">
            {debugMessages.map((msg, index) => (
              <li key={index}>• {msg}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Company Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name *
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.company_name ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.company_name && (
              <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax ID *
            </label>
            <input
              type="text"
              name="tax_id"
              value={formData.tax_id}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.tax_id ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.tax_id && (
              <p className="text-red-500 text-sm mt-1">{errors.tax_id}</p>
            )}
          </div>
        </div>
        
        {/* Company Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street *
            </label>
            <input
              type="text"
              name="company_address.street"
              value={formData.company_address.street}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors['company_address.street'] ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors['company_address.street'] && (
              <p className="text-red-500 text-sm mt-1">{errors['company_address.street']}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postcode *
            </label>
            <input
              type="text"
              name="company_address.postcode"
              value={formData.company_address.postcode}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors['company_address.postcode'] ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors['company_address.postcode'] && (
              <p className="text-red-500 text-sm mt-1">{errors['company_address.postcode']}</p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              name="company_address.city"
              value={formData.company_address.city}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors['company_address.city'] ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors['company_address.city'] && (
              <p className="text-red-500 text-sm mt-1">{errors['company_address.city']}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <input
              type="text"
              name="company_address.country"
              value={formData.company_address.country}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors['company_address.country'] ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors['company_address.country'] && (
              <p className="text-red-500 text-sm mt-1">{errors['company_address.country']}</p>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State/Region
          </label>
          <input
            type="text"
            name="company_address.state"
            value={formData.company_address.state}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        
        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Name *
            </label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.contact_name ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.contact_name && (
              <p className="text-red-500 text-sm mt-1">{errors.contact_name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.contact_email ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.contact_email && (
              <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <input
              type="tel"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.contact_phone ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.contact_phone && (
              <p className="text-red-500 text-sm mt-1">{errors.contact_phone}</p>
            )}
          </div>
        </div>
        
        {/* Captcha */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Captcha Verification *
          </label>
          {/* This would be replaced with an actual captcha component */}
          <div className="p-4 border border-gray-200 rounded bg-gray-50">
            <p className="text-sm text-gray-600 mb-2">
              Please complete the captcha to continue
            </p>
            {/* Placeholder for actual captcha component */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="captcha-checkbox"
                className="mr-2"
                onChange={(e) => handleCaptchaChange(e.target.checked ? 'verified' : '')}
              />
              <label htmlFor="captcha-checkbox" className="text-sm">
                I'm not a robot (demo captcha)
              </label>
            </div>
            {errors.captcha && (
              <p className="text-red-500 text-sm mt-1">{errors.captcha}</p>
            )}
          </div>
        </div>
        
        {/* Error display */}
        {errors.api && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700">
            {errors.api}
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex justify-between items-center mt-6">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          
          <button
            type="submit"
            className={`px-6 py-2 rounded text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Continue to Invoice'}
          </button>
        </div>
        
        {/* Button status explanation */}
        {!isFormValid() && (
          <div className="mt-2 text-sm text-gray-600">
            {captchaVerified ? (
              <p>Please complete all required fields to continue</p>
            ) : (
              <p>Please complete the captcha and all required fields to continue</p>
            )}
          </div>
        )}
      </form>
    </div>
  );
};